import { Plus, Trash2 } from 'lucide-react';
import {
  EQUIPMENT_STATUSES,
  EQUIPMENT_STATUS_LABEL,
  MAX_CREW_ROLES,
  MAX_EQUIPMENT_LINES,
  MAX_QUANTITY,
  type CrewCount,
  type CrewLine,
  type EquipmentLine,
  type EquipmentStatus,
} from '@obraplan/core/domain/planning/daily-report-crew';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';
import { useDraft } from '@/lib/use-draft';
import { ReportSection, SELECT_CLASS } from './DailyReportSection';

/**
 * "Equipe Envolvida" — o efetivo contado, e o corrigido (RN-212).
 *
 * ```text
 * Função                              Contado   No papel
 * ENGENHEIRO CIVIL                          0          1   ← não executa frente
 * PEDREIRO                                  3          3
 * SERVENTE                                  5          4   ← um saiu às 10h
 * ```
 *
 * A coluna **Contado** não é enfeite nem duplicidade: ela é o que o sistema sabe, e a
 * diferença entre ela e o que vai ao papel é informação por si só. Um corrigido
 * sistematicamente maior é gente no canteiro que o sistema não conhece — esta fase mostra
 * a diferença e não age sobre ela, que é o que o plano registra como risco.
 *
 * O engenheiro civil é o caso que manda no desenho: ele está no RDO de referência, não
 * executa frente, não tem conferência, e uma contagem automática o apagaria. Entra pela
 * correção, com contado zero — e o zero é a afirmação de que ele não apareceu em
 * conferência nenhuma, não um erro.
 *
 * Corrigir **para** zero também é correção: "hoje não tinha pedreiro" é uma frase, e não
 * um campo em branco. Por isso o campo aceita zero e o guarda.
 */

interface LinhaDeEfetivo {
  readonly roleName: string;
  readonly counted: number;
  readonly quantity: string;
  /** Verdadeiro na linha que a pessoa acrescentou — só essa tem o nome editável. */
  readonly nova: boolean;
}

function montarEfetivo(gravado: readonly CrewLine[]): readonly LinhaDeEfetivo[] {
  return gravado.map((linha) => ({
    roleName: linha.roleName,
    counted: linha.counted,
    quantity: String(linha.quantity),
    nova: false,
  }));
}

export function DailyReportCrew({
  crew,
  editable,
  isSaving,
  errorMessage,
  onSave,
}: {
  crew: readonly CrewLine[];
  editable: boolean;
  isSaving: boolean;
  errorMessage?: string | undefined;
  onSave: (crew: readonly CrewCount[]) => void;
}) {
  const [linhas, setLinhas] = useDraft(crew, montarEfetivo);

  const alterar = (indice: number, mudanca: Partial<LinhaDeEfetivo>): void => {
    setLinhas((atuais) =>
      atuais.map((linha, posicao) => (posicao === indice ? { ...linha, ...mudanca } : linha)),
    );
  };

  return (
    <ReportSection
      title="Equipe envolvida"
      description="O número nasce contado das conferências do dia. Corrija o que mudou e acrescente quem não executa frente — o engenheiro, o técnico de segurança."
      editable={editable}
      isSaving={isSaving}
      errorMessage={errorMessage}
      onSave={() => {
        // Vai a lista inteira, inclusive as linhas que ninguém tocou: o caso de uso
        // substitui o corrigido todo, e mandar só o que mudou apagaria o resto.
        onSave(
          linhas.map((linha) => ({
            roleName: linha.roleName,
            quantity: Number(linha.quantity === '' ? 0 : linha.quantity),
          })),
        );
      }}
      actions={
        <Button
          variant="secondary"
          size="sm"
          disabled={linhas.length >= MAX_CREW_ROLES}
          onClick={() => {
            setLinhas((atuais) => [
              ...atuais,
              { roleName: '', counted: 0, quantity: '1', nova: true },
            ]);
          }}
        >
          <Plus aria-hidden="true" />
          Função
        </Button>
      }
    >
      {linhas.length === 0 ? (
        <p className="text-sm text-(--color-content-muted)">
          Ninguém foi conferido neste dia ainda. Acrescente as funções à mão, ou confira o dia
          primeiro — o número vem sozinho.
        </p>
      ) : (
        <TableWrapper>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Função</TableHead>
                <TableHead className="w-24 text-right">Contado</TableHead>
                <TableHead className="w-32 text-right">No papel</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* A chave é a posição porque a linha não tem identidade própria: a lista é
                  gravada inteira, e o domínio a recebe como bloco. */}
              {linhas.map((linha, indice) => (
                <TableRow key={indice}>
                  <TableCell>
                    {linha.nova ? (
                      <Input
                        placeholder="Nome da função"
                        aria-label={`Nome da função acrescentada ${String(indice + 1)}`}
                        disabled={!editable}
                        value={linha.roleName}
                        onChange={(event) => {
                          alterar(indice, { roleName: event.target.value });
                        }}
                      />
                    ) : (
                      // A função contada vem do cadastro do funcionário, e renomeá-la aqui
                      // criaria uma segunda função com o mesmo povo dentro.
                      linha.roleName
                    )}
                  </TableCell>
                  <TableCell className="tabular text-right text-(--color-content-muted)">
                    {linha.counted}
                  </TableCell>
                  <TableCell className="text-right">
                    <Input
                      type="number"
                      min={0}
                      max={MAX_QUANTITY}
                      className="tabular w-24 text-right"
                      aria-label={`Efetivo de ${linha.roleName === '' ? `função ${String(indice + 1)}` : linha.roleName}`}
                      disabled={!editable}
                      value={linha.quantity}
                      onChange={(event) => {
                        alterar(indice, { quantity: event.target.value });
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {editable && linha.nova && (
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Remover a função acrescentada ${String(indice + 1)}`}
                        onClick={() => {
                          setLinhas((atuais) => atuais.filter((_, posicao) => posicao !== indice));
                        }}
                      >
                        <Trash2 aria-hidden="true" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableWrapper>
      )}
    </ReportSection>
  );
}

/**
 * "Equipamento Utilizado" — herdado do último RDO emitido, e corrigido (ADR-123).
 *
 * Não há cadastro de equipamento no sistema, e criá-lo é uma fase inteira. Em vez disso o
 * relatório novo nasce com as linhas do último **emitido** da obra, e quem relata corrige
 * o que mudou: cinco linhas quase sempre idênticas × 250 dias úteis deixam de ser 1.250
 * digitações e passam a ser cinco, mais as correções.
 *
 * A quantidade começa em **um**, e não em zero: quem não usou a serra hoje **apaga a
 * linha**, e não a zera. É o que faz a herança ser corrigível em vez de acumular lixo — e
 * é por isso que o domínio recusa quantidade zero aqui, ao contrário do efetivo.
 */

interface LinhaDeEquipamento {
  readonly code: string;
  readonly description: string;
  readonly status: EquipmentStatus;
  readonly quantity: string;
}

export function DailyReportEquipment({
  equipment,
  editable,
  isSaving,
  errorMessage,
  onSave,
}: {
  equipment: readonly EquipmentLine[];
  editable: boolean;
  isSaving: boolean;
  errorMessage?: string | undefined;
  onSave: (equipment: readonly EquipmentLine[]) => void;
}) {
  const [linhas, setLinhas] = useDraft(equipment, (gravado) =>
    gravado.map((linha) => ({
      code: linha.code ?? '',
      description: linha.description,
      status: linha.status,
      quantity: String(linha.quantity),
    })),
  );

  const alterar = (indice: number, mudanca: Partial<LinhaDeEquipamento>): void => {
    setLinhas((atuais) =>
      atuais.map((linha, posicao) => (posicao === indice ? { ...linha, ...mudanca } : linha)),
    );
  };

  return (
    <ReportSection
      title="Equipamento utilizado"
      description="Nasce com o do último RDO emitido desta obra. O que não foi usado hoje sai da lista."
      editable={editable}
      isSaving={isSaving}
      errorMessage={errorMessage}
      onSave={() => {
        onSave(
          linhas.map((linha) => ({
            description: linha.description,
            status: linha.status,
            quantity: Number(linha.quantity === '' ? 0 : linha.quantity),
            ...(linha.code.trim() === '' ? {} : { code: linha.code }),
          })),
        );
      }}
      actions={
        <Button
          variant="secondary"
          size="sm"
          disabled={linhas.length >= MAX_EQUIPMENT_LINES}
          onClick={() => {
            setLinhas((atuais) => [
              ...atuais,
              { code: '', description: '', status: 'IN_USE', quantity: '1' },
            ]);
          }}
        >
          <Plus aria-hidden="true" />
          Equipamento
        </Button>
      }
    >
      {linhas.length === 0 ? (
        <p className="text-sm text-(--color-content-muted)">
          Nenhum equipamento nesta obra ainda. A partir do primeiro RDO emitido, a lista vem
          preenchida sozinha.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {/* Posição como chave, pela mesma razão do efetivo: a lista vai inteira. */}
          {linhas.map((linha, indice) => (
            // No celular a linha quebra em duas, e sem o fio não se vê onde um
            // equipamento acaba e o outro começa. No monitor ela cabe inteira, e o fio
            // só acrescentaria ruído.
            <div
              key={indice}
              className="flex flex-wrap items-center gap-2 border-b border-(--color-border-subtle) pb-2 last:border-0 last:pb-0 md:border-0 md:pb-0"
            >
              <Input
                className="w-28"
                placeholder="Código"
                aria-label={`Código do equipamento ${String(indice + 1)}`}
                disabled={!editable}
                value={linha.code}
                onChange={(event) => {
                  alterar(indice, { code: event.target.value });
                }}
              />
              <Input
                className="min-w-48 flex-1"
                placeholder="Descrição"
                aria-label={`Descrição do equipamento ${String(indice + 1)}`}
                disabled={!editable}
                value={linha.description}
                onChange={(event) => {
                  alterar(indice, { description: event.target.value });
                }}
              />
              {/* Máquina parada é fato do dia, pelo mesmo raciocínio do turno impraticável:
                  o que aconteceu e o que isso custou são duas informações. */}
              <select
                className={`${SELECT_CLASS} w-40`}
                aria-label={`Situação do equipamento ${String(indice + 1)}`}
                disabled={!editable}
                value={linha.status}
                onChange={(event) => {
                  alterar(indice, { status: event.target.value as EquipmentStatus });
                }}
              >
                {EQUIPMENT_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {EQUIPMENT_STATUS_LABEL[status]}
                  </option>
                ))}
              </select>
              <Input
                type="number"
                min={1}
                max={MAX_QUANTITY}
                className="tabular w-20 text-right"
                aria-label={`Quantidade do equipamento ${String(indice + 1)}`}
                disabled={!editable}
                value={linha.quantity}
                onChange={(event) => {
                  alterar(indice, { quantity: event.target.value });
                }}
              />
              {editable && (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Remover o equipamento ${String(indice + 1)}`}
                  onClick={() => {
                    setLinhas((atuais) => atuais.filter((_, posicao) => posicao !== indice));
                  }}
                >
                  <Trash2 aria-hidden="true" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </ReportSection>
  );
}
